import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { articles } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 妖精视频`;
    } else {
      document.title = "文章未找到 - 妖精视频";
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categoryMap[article.category];
  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: category?.name || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        author: {
          "@type": "Person",
          name: article.author,
        },
        datePublished: article.publishDate,
        image: article.coverImage,
        url: `https://www.yaojingshipin.com/article/${article.id}`,
        publisher: {
          "@type": "Organization",
          name: "妖精视频",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbItems.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.label,
          item: item.href ? `https://www.yaojingshipin.com${item.href}` : undefined,
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount} 阅读
                </span>
              </div>
            </div>

            <div className="aspect-video rounded-lg overflow-hidden bg-background-100 mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
              {article.content.split("\n\n").map((paragraph, index) => {
                if (paragraph.startsWith("《") && paragraph.includes("》")) {
                  return (
                    <h2 key={index} className="text-lg font-heading text-foreground-950 mt-8 mb-4">
                      {paragraph}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="mb-4 text-sm leading-relaxed text-foreground-700">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">分享到：</span>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-all cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-accent-100 hover:text-accent-500 transition-all cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-secondary-100 hover:text-secondary-500 transition-all cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-sm"></i>
                </button>
              </div>

              <Link
                to="/"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>

            <div className="mt-10 p-5 rounded-lg bg-background-100/70 border border-background-200/70">
              <h3 className="text-base font-heading text-foreground-950 mb-3">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {articles
                  .filter((a) => a.id !== article.id && a.category === article.category)
                  .slice(0, 3)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="aspect-video rounded-lg overflow-hidden bg-background-200 mb-2">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}